import { useAppSelector, useAppDispatch } from "@/services/hooks";
import React, { useEffect, useMemo, useRef, useState } from "react";

import Select, { type MultiValue } from "react-select";
import { Link, useNavigate } from "react-router";
import ReactBSAlert from "react-bootstrap-sweetalert";
import {
  saveUser,
  registerUser,
  restoreUser,
  releaseUser,
  clearUser,
} from "@/services/userServices/userActions";
import { useImageUpload } from "@/services/ImageServices/useImageUpload";
import { setErrors } from "@/services/errorServices/errorAction";
import { COUNTRY_OPTIONS, type SelectOption } from "@/utils/selectOptions";
import { Col, Label, FormGroup, Form } from "reactstrap";
import CountryAccessDisplayContainer from "./CountryAccessDisplayContainer";
import type { PickedImage } from "./ImageUploadContainer";
import { asCaught } from "@/services/caughtError";

/** The first validation error of a register 409 (SCRUM-1663) as this screen reads it. */
type RegisterRejection = {
  errors?: { deletedId?: unknown; value?: unknown; msg?: unknown }[];
};

export type AdvancedPermissionContainerProps = {
  /** The avatar picked on the personal-information step, if any. */
  image?: PickedImage;
};

export default function AdvancedPermissionContainer({ image }: AdvancedPermissionContainerProps) {
  const dispatch = useAppDispatch();
  const uploadImage = useImageUpload();

  const userFromStore = useAppSelector((s) => s.users.register_user);

  const [user, setUser] = useState(userFromStore || {});
  const [countrycode, setCountrycode] = useState<readonly SelectOption[]>([]);
  // SCRUM-1887 (same defect as SCRUM-1885): redirect with navigate(), not a
  // `redirect` state rendered as <Navigate>. An instance that survived a quick
  // browser Back still held that state and rendered nothing.
  const navigate = useNavigate();
  // The post-register timer is cleared on unmount, so an editor who leaves
  // during that second is not pulled to /admin/users.
  const redirectTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(redirectTimer.current), []);
  const [alert, setAlert] = useState<React.ReactNode>(null);
  // SCRUM-1663: set when register answers 409 because a soft-deleted user still
  // holds the e-mail; the form then offers to restore that account instead.
  const [deletedHolder, setDeletedHolder] = useState<
    { id: string; email: string; msg: string } | null
  >(null);

  // Always track the latest store value. onSubmit can fire before the component
  // re-renders after the last permission dispatch; without this ref onSubmit would
  // use a stale `user` and permission updates would be lost (user saved without
  // any permissions).
  const latestUserRef = useRef(userFromStore);
  useEffect(() => {
    latestUserRef.current = userFromStore;
  });

  useEffect(() => {
    if (userFromStore) {
      setUser(userFromStore);
      if (Array.isArray(userFromStore.countrycode)) {
        setCountrycode(userFromStore.countrycode);
      }
    }
  }, [userFromStore]);

  const pushUserToStore = (nextCountrycode: readonly SelectOption[]) => {
    const pickedCodes = (nextCountrycode || []).map((c) => c.value);
    const filteredPerm =
      (user.permission || []).filter((p: { CountryCode: string }) => pickedCodes.includes(p.CountryCode)) || [];

   
    dispatch(
      saveUser({
        name: user.name,
        email: user.email,
        emailverif: user.emailverif,
        countrycode: nextCountrycode,
        role: user.role,
        enabled: user.enabled,
        avatar: user.avatar,
        permission: filteredPerm,
      })
    );
  };

  const onSelectCountries = (selected: MultiValue<SelectOption>) => {
    setCountrycode(selected || []);
    pushUserToStore(selected || []);
  };

  const onHideAlert = () => {
    setAlert(null);
    navigate("/admin/users");
  };

  const showTemporaryPassword = (email: string, temporaryPassword: string) => {
    setAlert(
      // @ts-expect-error react-bootstrap-sweetalert prop-types (customIcon/btnSize)
      <ReactBSAlert
        style={{ display: "block", marginTop: "-100px" }}
        title="User created"
        onConfirm={onHideAlert}
        onCancel={onHideAlert}
        confirmBtnBsStyle="success"
        btnSize=""
      >
        <p>
          Temporary password for <strong>{email}</strong>:
        </p>
        <p>
          <code>{temporaryPassword}</code>
        </p>
        <p>The user must change this password on first login.</p>
      </ReactBSAlert>
    );
  };

  /** `e` is absent when Start fresh re-runs the submit after the release. */
  const onSubmit = async (e?: React.SyntheticEvent) => {
    e?.preventDefault();

    // Use the latest store value (ref), not the possibly stale local `user`
    // state — this prevents just-set permissions from being lost.
    const currentUser = latestUserRef.current || user;

    const hasCountries =
      Array.isArray(currentUser?.countrycode) && currentUser.countrycode.length > 0;
    if (!hasCountries) {
      return;
    }

    // Linearised (SCRUM-1021): when an avatar file is picked, upload it via the
    // shared useImageUpload hook and register inline with the returned URL —
    // replacing the old state.images.url watch-effect that ran the register.
    // Both register call-sites (image / no-image) now live here.
    let avatar;
    if (image?.data) {
      const path = `/USERS/${image.imgName}`;
      try {
        avatar = await uploadImage(image.data, path);
      } catch (err) {
        // A failed avatar upload re-throws a plain Error (SCRUM-815) that routes
        // nowhere; this view surfaces errors only through the central hub, so
        // push it into state.errors. (registerUser failures below already
        // surface via .unwrap()/appErrorMiddleware.)
        dispatch(
          setErrors({
            message:
              asCaught(err)?.message || "Uploading the avatar failed. Please try again.",
          })
        );
        return;
      }
    } else {
      avatar = image?.secure_url ? image.secure_url : "default";
    }

    const payload = { ...currentUser, avatar };

    try {
      // SCRUM-840: .unwrap() so registration failures (e.g. a validation 400)
      // surface via the central hub and skip the clear/redirect success path.
      setDeletedHolder(null);
      const result = await dispatch(registerUser(payload)).unwrap();
      dispatch(clearUser());
      if (result?.temporaryPassword) {
        showTemporaryPassword(payload.email, result.temporaryPassword);
      } else {
        redirectTimer.current = setTimeout(() => navigate("/admin/users"), 1000);
      }
    } catch (err) {
      // The central hub has already toasted the error. One body earns more than
      // a toast: the SCRUM-1663 409 names the soft-deleted user that still holds
      // this e-mail, so offer to restore it here instead of leaving a dead end.
      const first = (err as RegisterRejection | undefined)?.errors?.[0];
      if (first?.deletedId) {
        setDeletedHolder({
          id: String(first.deletedId),
          email: String(first.value ?? payload.email),
          msg: String(first.msg ?? "A deleted user holds this e-mail address"),
        });
      }
    }
  };

  const onRestore = async () => {
    if (!deletedHolder) return;
    try {
      await dispatch(restoreUser(deletedHolder.id)).unwrap();
      dispatch(clearUser());
      navigate("/admin/users");
    } catch {
      /* central hub surfaces the restore error; the offer stays on screen */
    }
  };

  // SCRUM-1816 "Start fresh": free the deleted user's e-mail (it stays deleted,
  // renamed), then register the new account again. Release first — a re-submit
  // without it would only get the same 409 back.
  const onStartFresh = async () => {
    if (!deletedHolder) return;
    try {
      await dispatch(releaseUser(deletedHolder.id)).unwrap();
    } catch {
      /* central hub surfaces the release error; the offer stays on screen */
      return;
    }
    await onSubmit();
  };

  const countriesForm = useMemo(() => {
    return (countrycode || []).map((c) => (
      <CountryAccessDisplayContainer
        key={c.value}
        name={c.label}
        value={c.value}
        oldPermissionUpdateForm={user.permission}
      />
    ));
  }, [countrycode, user.permission]);

  return (
    <>
      {alert}
      <Form>
        <FormGroup className="row">
          <Label className="form-control-label" md="2">
            Select Country Access
          </Label>
          <Col md="10">
            <Select
              isMulti
              value={countrycode}
              options={COUNTRY_OPTIONS}
              className="basic-multi-select"
              classNamePrefix="select"
              onChange={onSelectCountries}
            />
          </Col>
        </FormGroup>
      </Form>

      {countriesForm}

      {deletedHolder && (
        <div className="alert alert-warning" role="alert">
          {deletedHolder.msg} (<strong>{deletedHolder.email}</strong>). Restoring
          brings that account back with its previous role and permissions;
          Start fresh keeps it deleted and creates this new account under the
          e-mail address.
          <button
            type="button"
            className="btn btn-sm btn-warning ml-3"
            onClick={onRestore}
          >
            Restore deleted user
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-warning ml-2"
            onClick={onStartFresh}
          >
            Start fresh
          </button>
        </div>
      )}

      <button
        className="btn btn-outline-primary mr-1 float-right"
        type="submit"
        onClick={onSubmit}
      >
        Finish Adding
      </button>

      <Link to="/admin/users" className="btn btn-outline-default mr-1 float-left">
        Back To Users List
      </Link>
    </>
  );
}
